<template>
  <div
    v-if="unsupportedFormat"
    :data-after-content="'.' + attachmentFormat"
    class="application attachment"
    :class="{ 'attachment-ipv': inPanelView }"
    @click="$emit('downloadFile', attachment)"
  >
    <div class="application-wrap" :data-after-content="'.' + attachmentFormat">
      <div class="circle">
        <BlueDocIcon />
      </div>
    </div>
  </div>
  <div
    v-else
    :class="[{'application': unsupportedFormat, 'attachment-ipv': inPanelView }, attachmentType]"
    :data-after-content="'.' + attachmentFormat"
    class="attachment"
  >
    <div v-if="!inPanelView" class="check" :style="{ opacity: attachment.isActive ? '1' : '' }">
      <CustomCheckbox
        :id="createUniqKey(attachment.uniqKey)"
        :input-element="attachment"
        :item="attachment"
        @checked="changeCheckbox"
      />
    </div>
    <div
      v-if="!inPanelView"
      class="t-dot-menu"
      :style="{ opacity: $refs.contextMenu && $refs.contextMenu.visible ? '1' : '' }"
    >
      <ContextMenu
        ref="contextMenu"
        v-click-outside="hipeOptionsMenu"
        :item="attachment"
        :options="optionsMenu"
        @onSelected="handleMenuOptionSelect"
      />
    </div>
    <div v-if="attachment.isPinned && !inPanelView" class="pinned-icon" @click="unpinMedia">
      <img :src="pinnedIcon" alt="pinned">
    </div>
    <div v-if="inPanelView" class="attachment-format-sign attachment-format-sign-ipv">
      <div class="file-name">
        <span>{{ attachment.name.substring(0, attachment.name.lastIndexOf('.')) }}</span>
      </div>
      <span>{{ attachment.name.substring(attachment.name.lastIndexOf('.')) }}</span>
    </div>
    <div
      v-if="attachmentType === 'application' || unsupportedFormat"
      class="application-wrap"
      :data-after-content="'.' + attachmentFormat"
    >
      <div class="circle">
        <BlueDocIcon />
      </div>
    </div>
    <div
      v-else-if="attachment.isBlured"
      :style="{
        maxWidth: inPanelView ? '380px' : '150px',
        maxHeight: inPanelView ? '256px' : '100px',
      }"
      class="blured"
      @click="openImage"
    >
      <MagnifyingGlassIcon40 />
    </div>
    <div
      v-else-if="attachmentType === 'image' && !inPanelView"
      @click="openImage"
    >
      <div v-if="isHeicFormat && isConverting(attachment.link)" class="conversion-loader">
        <CircularLoader make-small />
      </div>
      <img
        v-else
        :src="displayImageUrl"
        alt="attachment"
        :style="{ maxWidth: '150px', maxHeight: '100px' }"
        style="min-height: 100px;"
      >
    </div>
    <div
      v-else-if="attachmentType === 'image' && inPanelView"
      class="panel-image-wrapper"
      @click="openImage"
    >
      <div v-if="isHeicFormat && isConverting(attachment.link)" class="conversion-loader panel-conversion-loader">
        <CircularLoader />
      </div>
      <template v-else>
        <img
          :src="displayImageUrl"
          alt="attachment"
          class="panel-image"
        >
        <MagnifyingGlassIcon40 />
      </template>
    </div>
    <div v-else-if="attachmentType === 'video'" class="video-wrap">
      <div
        class="player-wrap"
        :style="{ width: inPanelView ? '380px' : '150px', height: inPanelView ? '240px' : '100px' }"
        @click="openImage"
      >
        <div v-if="!inPanelView" class="on-top-wrap" />
        <div v-if="!isPlayerReady" class="player-placeholder">
          <span class="loading-indicator">Loading video...</span>
        </div>
        <vue-plyr v-if="isPlayerReady && attachment.link" :key="`thumbnail-${attachment.id}`">
          <video
            :width="inPanelView ? '380' : '150'"
            :height="inPanelView ? '240' : '100'"
            @error="handleVideoError"
          >
            <source
              :src="attachment.link"
              class="video"
            >
          </video>
        </vue-plyr>
      </div>
    </div>
    <div v-if="!inPanelView" class="attachment-format-sign">
      <div class="file-name">
        <span>{{ attachment.name.substring(0, attachment.name.lastIndexOf('.')) }}</span>
      </div>
      <span>{{ attachment.name.substring(attachment.name.lastIndexOf('.')) }}</span>
    </div>
    <div v-if="!inPanelView && tagsList.length" class="tag-wrap">
      <span
        v-for="(tag, index) in tagsList.slice(0, 2)"
        :key="index"
        class="tag"
        v-text="tag"
      />
      <span v-if="tagsList.length > 2" class="tag" style="text-transform: lowercase">
        {{ '+' + (tagsList.length - 2).toString() + (tagsList.length - 2 > 1 ? ' tags' : ' tag') }}
      </span>
    </div>
    <MetadataPopup ref="itemDataPopup" :file-metadata="fileMetadata" />
  </div>
</template>

<script>
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import ContextMenu from '@/components/popups/ContextMenu.vue';
import redactIcon from '@/assets/img/case-messages/redacted.png';
import priorityIcon from '@/assets/img/case-messages/priority.png';
import exportIcon from '@/assets/img/case-messages/export-icon.png';
import infoIcon from '@/assets/img/case-messages/info.png';
import burgerIcon from '@/assets/img/case-messages/burger.png';
import MetadataPopup from '@/components/popups/MetadataPopup.vue';
import {mapActions, mapGetters} from 'vuex';
import mediaTagging from '@/mixins/mediaTagging';
import heicConversion from '@/mixins/heicConversion';
import deleteIcon from '@/assets/img/case-messages/delete.svg';
import unredactIcon from '@/assets/img/case-messages/unredact.svg';
import pinnedIcon from '@/assets/img/case-messages/pinned.png';
import hasPermission from '@/utils/permission';
import BlueDocIcon from '@/components/icons/BlueDocIcon.vue';
import MagnifyingGlassIcon40 from '@/components/icons/MagnifyingGlassIcon40.vue';
import VuePlyr from 'vue-plyr';
import CircularLoader from '@/components/loader/CircularLoader.vue';

export default {
  name: 'GalleryItem',
  components: {
    MagnifyingGlassIcon40,
    BlueDocIcon,
    MetadataPopup,
    ContextMenu,
    CustomCheckbox,
    VuePlyr,
    CircularLoader
  },
  emits: ['changeCheckbox', 'exportItem', 'updateMedia', 'openMediaPopup'],
  mixins: [mediaTagging, heicConversion],
  props: {
    attachment: {
      type: Object,
      default: () => {
      },
    },
    inPanelView: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    const menuOptions = [
      {id: 1, name: 'Mark priority', img: priorityIcon},
      {id: 2, name: 'Redact media', img: redactIcon},
      {id: 3, name: 'Export', img: exportIcon},
      {id: 3, name: 'Show Meta', img: infoIcon},
      {id: 3, name: 'View Details', img: burgerIcon},
    ];
    return {
      pinnedIcon,
      menuOptions,
      fileMetadata: null,
      attachmentType: null,
      attachmentFormat: null,
      tagsList: [],
      isPlayerReady: false,
    };
  },
  computed: {
    ...mapGetters('mediaTagging', {
      isTaggingActive: 'isTaggingActive',
      taggingItem: 'getTaggingItem',
    }),
    ...mapGetters('mediaConversion', {
      isConverting: 'isConverting',
    }),
    unsupportedFormat() {
      switch (this.attachmentFormat) {
        case 'ogg':
        case 'mp4':
        case 'ogv':
        case 'webm':
        case 'jpeg':
        case 'jpg':
        case 'png':
        case 'gif':
        case 'webp':
        case 'heic':
        case 'heif':
          return false;
        default:
          return true;
      }
    },
    textVersion() {
      return this.attachment.textVersion;
    },
    optionsMenu() {
      let options = this.menuOptions;

      if (this.attachment.isPinned) {
        options
          .filter(option => option.name === 'Mark priority')
          .map(option => {
            option.name = 'Remove from Priorities';
            option.img = deleteIcon;
          });
      } else {
        options
          .filter(option => option.name === 'Remove from Priorities')
          .map(option => {
            option.name = 'Mark priority';
            option.img = priorityIcon;
          });
      }


      if (!this.hasPermission(['add_comments_highlights'])) {
        options = options.filter(option =>
          option.name.toLowerCase() !== 'redact media' && option.name.toLowerCase() !== 'unredact media');
      } else {
        if (this.attachment.isBlured) {
          options
            .filter(option => option.name === 'Redact media')
            .map(option => {
              option.name = 'Unredact media';
              option.img = unredactIcon;
            });
        } else {
          options
            .filter(option => option.name === 'Unredact media')
            .map(option => {
              option.name = 'Redact media';
              option.img = redactIcon;
            });
        }
      }

      return options;
    },
    displayImageUrl() {
      if (this.isHeicFormat(this.attachment.name)) {
        const convertedUrl = this.getConvertedImage(this.attachment.link);
        if (convertedUrl) {
          return convertedUrl;
        }
      }
      return this.attachment.link;
    }
  },
  watch: {
    attachment: {
      handler() {
        this.getAttachmentType();
        this.tagsList = this.attachment.tags && this.attachment.tags.length > 0
          ? [...this.attachment.tags.map(tag => tag)]
          : [];
        this.triggerHeicConversion();
      },
      deep: true
    }
  },
  mounted() {
    this.getAttachmentType();
    this.tagsList = this.attachment.tags || [];
    this.initializeVideoPlayer();
    this.triggerHeicConversion();
  },
  beforeDestroy() {
    this.isPlayerReady = false;
  },
  activated() {
    this.initializeVideoPlayer();
  },
  deactivated() {
    this.isPlayerReady = false;
  },
  updated() {
    this.initializeVideoPlayer();
  },
  methods: {
    hasPermission,
    ...mapActions('mediaTagging', {
      setTaggingItem: 'setTaggingItem',
      setInitialState: 'setInitialState',
    }),
    async handleMenuOptionSelect(optionName) {
      switch (optionName.toLocaleLowerCase()) {
        case 'export':
          this.$emit('exportItem', this.attachment.id);
          break;
        case 'mark priority':
          if (!this.attachment.isPinned) {
            await this.pinGallery([this.attachment.id]);
          } else {
            await this.unPinGallery([this.attachment.id]);
          }
          this.$emit('updateMedia');
          break;
        case 'remove from priorities':
          await this.unPinGallery([this.attachment.id]);
          this.$emit('updateMedia');
          break;
        case 'redact media':
          if (!this.attachment.isBlured) {
            await this.blurGallery(this.attachment.id);
          } else {
            await this.unBlurGallery(this.attachment.id);
          }
          this.$emit('updateMedia');
          break;
        case 'unredact media':
          await this.unBlurGallery(this.attachment.id);
          this.$emit('updateMedia');
          break;
        case 'show meta':
          this.openMetadataPopup();
          break;
        case 'view details':
          this.gotoTagging();
          break;
      }
    },
    async unpinMedia() {
      await this.unPinGallery([this.attachment.id]);
      this.$emit('updateMedia');
    },
    hipeOptionsMenu() {
      if (this.$refs.contextMenu) {
        this.$refs.contextMenu.hideMenu();
      }
    },
    gotoTagging() {
      if (this.isTaggingActive) {
        this.setTaggingItem(this.attachment);
      } else {
        this.setInitialState({
          caseId: this.caseId,
          clientId: this.$route.query.publicId,
          taggingItem: this.attachment,
        });
        if (this.$route.name !== 'Media tagging main tab') {
          this.$router.push({name: 'Media tagging main tab'});
        }
      }
    },
    openMetadataPopup() {
      this.$load(async () => {
        const response = await this.$api.media.getGalleryData({
          caseId: this.caseId,
          galleryId: this.attachment.id
        });
        this.fileMetadata = response.data.body;
      });
      this.$refs.itemDataPopup.openPopups();
    },

    getAttachmentType() {
      this.attachmentType = this.attachment.type
        ? this.attachment.type.split('/')[0].toLowerCase()
        : this.getFileType(this.attachment.name);
      this.attachmentFormat = this.attachment.name.split('.').reverse()[0].toLowerCase();
    },
    getFileType(fileName) {
      const extension = fileName.split('.').pop().toLowerCase();

      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic', 'heif'];

      const videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm', 'flv'];

      if (imageExtensions.includes(extension)) {
        return 'image';
      } else if (videoExtensions.includes(extension)) {
        return 'video';
      } else {
        return 'other';
      }
    },
    async triggerHeicConversion() {
      if (this.attachment && this.attachment.link && this.isHeicFormat(this.attachment.name)) {
        if (!this.getConvertedImage(this.attachment.link) && !this.isConverting(this.attachment.link)) {
          try {
            await this.getDisplayImageUrl(this.attachment);
          } catch (error) {
            console.error('Automatic HEIC conversion failed:', error);
          }
        }
      }
    },
    async openImage() {
      const displayUrl = await this.getDisplayImageUrl(this.attachment);
      if (this.isHeicFormat(this.attachment.name) && displayUrl !== this.attachment.link) {
        this.$emit('openMediaPopup', this.attachment.id, {jpegUrl: displayUrl});
      } else {
        this.$emit('openMediaPopup', this.attachment.id);
      }
    },
    changeCheckbox(item) {
      this.$emit('changeCheckbox', item);
    },
    handleVideoError() {
      console.warn('Video player failed to initialize or load video');
    },

    initializeVideoPlayer() {
      if (this.attachmentType === 'video' && this.attachment.link && !this.isPlayerReady) {
        setTimeout(() => {
          this.isPlayerReady = true;
        }, 100);
      }
    },
  },
};
</script>
<style scoped>
.application {
  position: relative;
  width: 170px;
  min-height: 115px;
}

.application {
  display: flex;
  align-items: flex-start;
}

.application .application-wrap {
  cursor: pointer;
  background-color: var(--file-background);
  max-width: 170px;
  height: 115px;
  justify-content: center;
}

.circle {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white-color);
  border-radius: 50%;
  width: 59px;
  height: 59px;
}

.application-wrap:after,
.application:not(.with-recognition-text).image:after,
.application:not(.with-recognition-text).video:after {
  content: attr(data-after-content);
  position: absolute;
  right: 8px;
  bottom: 8px;
  border-radius: 79px;
  padding: 3px 11px;
  background-color: #F8FCFC;
  border: 1px solid #D7DCE6;
  font-size: 12px;
  font-weight: 500;
  line-height: 160%;
  color: #373c44;
}

.video:after {
  top: 150px;
  right: unset;
  bottom: unset;
  left: 270px;
  background-color: #373C44;
  border: none;
  color: #fff;
}

.application-wrap,
.video {
  position: relative;
}

.image img {
  width: 100%;
  height: 100%;
  max-height: 240px;
  object-fit: cover;
  cursor: pointer;
}

.attachment-ipv .image img {
  object-fit: contain;
}

.attachment:not(:last-child) {
  margin-bottom: 8px;
}

.attachment {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: fit-content;
  overflow: hidden;
  margin-right: 12px;
  position: relative;
  cursor: pointer;
}

.attachment-ipv {
  width: 100%;
  max-width: 380px;
  height: 256px;
}


.application-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.video-wrap, .player-wrap {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.video-wrap .player-wrap {
  width: 150px;
  height: 98px;
  min-height: 98px;
  position: relative;
}

.video-wrap >>> .plyr {
  flex-grow: 1;
  border-radius: 8px;
}

.video-wrap >>> .plyr video {
  object-fit: cover;
}

.attachment-ipv .video-wrap >>> .plyr video {
  object-fit: contain;
}

.video-wrap >>> .plyr {
  left: 10px;
}

.check {
  opacity: 0;
  position: absolute;
  z-index: 2;
  top: 5px;
  left: 5px;
  transition: all .3s;
  border-radius: 2px;
}

.t-dot-menu {
  opacity: 0;
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  border: 0.5px solid var(--case-btn-border-color);
  border-radius: 2px;
  background: var(--gray-bg-color-2);
  transition: all .3s;
}

.attachment:hover .check,
.attachment:hover .t-dot-menu {
  opacity: 1;
}

.attachment-format-sign {
  display: flex;
  align-items: center;
  gap: 1px;
  width: 100%;
  max-width: 200px;
}

.attachment-format-sign .file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  max-width: 350px;
}

/*
.attachment-format-sign {
  position: absolute;
  text-align: center;
  width: 46px;
  height: 25px;
  top: 86px;
  left: 122px;
  padding: 2px 0 0 0;
  border-radius: 79px;
  border: 0.5px solid var(--case-btn-border-color);
  background: var(--gray-bg-color-2);
}
*/

.attachment-format-sign span {
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  color: var(--secondary-black-color);
}

.attachment-format-sign-ipv {
  max-width: 440px;
  padding: 0 0 0 15px;
}

.attachment-format-sign-ipv span {
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  text-align: center;
  vertical-align: middle;
  color: var(--secondary-black-color);
}

.pinned-icon {
  position: absolute;
  top: 70px;
  right: 5px;
  cursor: pointer;
  z-index: 1;
}

.pinned-icon img {
  max-width: 24px;
  max-height: 24px;
  width: 100%;
}

.tag-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-right: 15px;
}

.tag {
  font-weight: 500;
  font-size: 10px;
  line-height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #FFFFFF;
  padding: 2px 4px;
  background-color: #F1105F;
  text-transform: capitalize;
  border-radius: 4px;
  margin-bottom: 4px;
}

.tag:not(:last-child) {
  margin-right: 8px;
}


.panel-image-wrapper {
  width: 100%;
  max-width: 380px;
  max-height: 256px;
  height: 100%;
  cursor: pointer;
}

.panel-image-wrapper svg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: all .3s;
  opacity: 0;
}

.panel-image-wrapper:hover svg {
  opacity: 1;
}

.blured {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: #000;
  width: 100%;
  height: 100%;
  min-height: 100px;
}

.blured svg {
  opacity: 0;
  transition: all .5s;
}

.blured:hover svg {
  opacity: 1;
}

.on-top-wrap {
  position: absolute;
  width: 150px;
  height: 100px;
  z-index: 1;
  top: 0;
  left: 0;
}

.player-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  border-radius: 8px;
}

.loading-indicator {
  font-size: 12px;
  color: #666;
}

.panel-image {
  max-width: 380px;
  width: 100%;
  height: 235px !important;
  max-height: 235px;
  min-height: 100px;
}

.conversion-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 100px;
  min-height: 100px;
  background-color: #f8f8f8;
  border-radius: 8px;
}

.panel-conversion-loader {
  width: 100%;
  max-width: 380px;
  height: 235px;
  max-height: 235px;
}

</style>
